import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, MapPin, Linkedin, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_plastic-wealth/artifacts/guwzj2f5_2.png';

export default function Footer() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" data-testid="footer-section" className="relative pt-24 pb-8">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1E2E] to-[#071520]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Contact Form + Info */}
        <div className="grid lg:grid-cols-2 gap-16 mb-20">
          {/* Contact Form */}
          <div>
            <h3 className="text-2xl md:text-3xl font-semibold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('contact.title')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  type="text"
                  placeholder={t('contact.name')}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#42D2D9]/50 transition-colors"
                />
                <input
                  type="email"
                  placeholder={t('contact.email')}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#42D2D9]/50 transition-colors"
                />
              </div>
              <input
                type="tel"
                placeholder={t('contact.phone')}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="contact-phone"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#42D2D9]/50 transition-colors"
              />
              <textarea
                placeholder={t('contact.message')}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                required
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#42D2D9]/50 transition-colors resize-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="cta-primary inline-flex items-center gap-2"
              >
                <Send className="w-4 h-4 relative z-10" />
                <span>{loading ? '...' : t('contact.send')}</span>
              </button>
              {status === 'success' && (
                <p data-testid="contact-success" className="text-sm text-[#42D2D9]">{t('contact.success')}</p>
              )}
              {status === 'error' && (
                <p data-testid="contact-error" className="text-sm text-red-400">{t('contact.error')}</p>
              )}
            </form>
          </div>

          {/* Footer info */}
          <div className="space-y-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="CSR & Finance" className="h-8 w-8" />
                <span className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('footer.brand')}
                </span>
              </div>
              <p className="text-sm text-slate-400">{t('footer.tagline')}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {/* Services */}
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-widest mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('footer.services')}
                </h4>
                <ul className="space-y-2">
                  <li className="text-sm text-slate-400">{t('footer.service1')}</li>
                  <li className="text-sm text-slate-400">{t('footer.service2')}</li>
                  <li className="text-sm text-slate-400">{t('footer.service3')}</li>
                  <li className="text-sm text-slate-400">{t('footer.service4')}</li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-widest mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('footer.contact')}
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#42D2D9]" />
                    <span className="text-sm text-slate-400">{t('footer.email')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#42D2D9]" />
                    <span className="text-sm text-slate-400">{t('footer.location')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-[#42D2D9]" />
                    <span className="text-sm text-slate-400">LinkedIn</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-widest mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('footer.legal')}
              </h4>
              <div className="flex flex-wrap gap-4">
                <span className="text-xs text-slate-500 hover:text-slate-300 cursor-pointer transition-colors">{t('footer.privacy')}</span>
                <span className="text-xs text-slate-500 hover:text-slate-300 cursor-pointer transition-colors">{t('footer.terms')}</span>
                <span className="text-xs text-slate-500 hover:text-slate-300 cursor-pointer transition-colors">{t('footer.cookies')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/5 pt-8">
          <p className="text-xs text-slate-600 text-center mb-2">{t('footer.rights')}</p>
          <p className="text-xs text-slate-600 text-center max-w-2xl mx-auto">{t('footer.disclaimer')}</p>
        </div>
      </div>
    </footer>
  );
}
